// The chat box's browser-run turn (claude-docs/2026-10-31-chat-app-actions-
// plan.md): held by ChatProvider, which sits above the router's pages, so a
// turn outlives the popup — an action that opens another page closes it.
// Admins only while it is tried; an admin can switch back to the server-run
// chat, and the choice is kept in this browser.
// Its engine (browserTurn.js: the page runner, the action catalog, the
// screenshot code) loads on the first send — ChatProvider is in every page's
// first download.
import { useCallback, useContext, useRef, useState } from 'react';
import AdminContext from '../../contexts/AdminContext';
import { chatErrorMessage } from '../../services/chatErrorMessage';
import { keepChatForReload } from './chatAcrossReload';
import { historyFor, trimToBudget } from './chatTranscript';

const PREF_KEY = 'chat.browserLoop';
/** Said after a reply whose action reloads the page. */
const RELOADING = 'The page is reloading to show the change.';

const readPref = () => {
  try {
    return window.localStorage.getItem(PREF_KEY) !== 'off';
  } catch {
    return true;
  }
};
const writePref = (on) => {
  try {
    window.localStorage.setItem(PREF_KEY, on ? 'on' : 'off');
  } catch {
    // the choice lasts this page only
  }
};

const newId = () => `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;

/**
 * @param {{ messages, addMessage, setIsLoading, setProgress, conversationContext }} chat
 *   ChatProvider's own state
 * @param {object|null} chat.restoredConversation  the conversation a page
 *   reload kept (chatAcrossReload.js)
 */
export function useChatTurn({
  messages,
  addMessage,
  setIsLoading,
  setProgress,
  conversationContext,
  restoredConversation = null,
}) {
  const admin = useContext(AdminContext);
  const available = admin?.isAdmin === true;
  const [pref, setPref] = useState(readPref);
  const [pendingConfirm, setPendingConfirm] = useState(null);
  const [turnRunning, setTurnRunning] = useState(false);
  const running = useRef(false);
  const stopped = useRef(false);
  const answer = useRef(null);
  // The last turn's whole conversation and the chat message it ended on:
  // what the next turn carries (chatTranscript.historyFor).
  const conversation = useRef(restoredConversation);
  const latest = useRef({});
  latest.current = { messages, conversationContext };

  const setOn = useCallback((on) => {
    writePref(on);
    setPref(on);
  }, []);

  const answerConfirm = useCallback((allow) => {
    const resolve = answer.current;
    answer.current = null;
    setPendingConfirm(null);
    resolve?.(allow === true);
  }, []);

  const stopTurn = useCallback(() => {
    stopped.current = true;
    answerConfirm(false);
  }, [answerConfirm]);

  const sendBrowserTurn = useCallback(
    async (text) => {
      if (running.current) return;
      running.current = true;
      stopped.current = false;
      setTurnRunning(true);
      const before = latest.current.messages;
      const chatContext = latest.current.conversationContext;
      const asked = {
        id: newId(),
        role: 'user',
        content: text,
        timestamp: new Date(),
      };
      addMessage(asked);
      setIsLoading(true);
      setProgress({ status: 'executing', message: 'Working…', activity: [] });
      const reply = (content, activity) => {
        const message = {
          id: newId(),
          role: 'assistant',
          content,
          activity: activity?.length ? activity : undefined,
          timestamp: new Date(),
        };
        addMessage(message);
        return message;
      };
      try {
        const { runBrowserTurn } = await import('./browserTurn');
        const out = await runBrowserTurn({
          text,
          history: trimToBudget(historyFor(conversation.current, before)),
          chatContext,
          readLocation: () => latest.current.conversationContext?.location,
          confirm: (pending) =>
            new Promise((resolve) => {
              answer.current = resolve;
              setPendingConfirm(pending);
            }),
          onActivity: (activity) =>
            setProgress({ status: 'executing', message: 'Working…', activity }),
          isStopped: () => stopped.current,
        });
        const said = out.reply || (stopped.current ? 'Stopped.' : 'Done.');
        const keep = (last) => {
          conversation.current = {
            transcript: out.transcript,
            coveredId: last.id,
          };
        };
        if (out.afterAnswer) {
          // The page goes away: keep the conversation for the next one.
          const last = reply(`${said}\n\n${RELOADING}`, out.activity);
          keep(last);
          keepChatForReload([...before, asked, last], conversation.current);
          out.afterAnswer();
        } else {
          keep(reply(said, out.activity));
        }
      } catch (error) {
        // What earlier rounds did has happened: keep it with the error. The
        // conversation stays as it was; the next turn carries this one's
        // request and error as lines of text.
        const partial = error?.partial;
        reply(
          [partial?.reply, chatErrorMessage(error)]
            .filter(Boolean)
            .join('\n\n'),
          partial?.activity
        );
      } finally {
        running.current = false;
        answer.current = null;
        setPendingConfirm(null);
        setTurnRunning(false);
        setIsLoading(false);
        setProgress(null);
      }
    },
    [addMessage, setIsLoading, setProgress]
  );

  return {
    browserLoop: { available, on: available && pref, setOn },
    sendBrowserTurn,
    pendingConfirm,
    answerConfirm,
    stopTurn,
    turnRunning,
  };
}
